/**
 * Parses CSV data into a format compatible with the application
 * 
 * @param {string} csvText - The CSV text data
 * @returns {Object} Parsed data with columns and rows
 */
const csvToDataFrame = (csvText) => {
  if (!csvText || typeof csvText !== 'string') {
    throw new Error('Invalid CSV data received');
  }

  try {
    // Parse CSV text
    const lines = csvText.trim().split('\n');
    if (lines.length === 0) {
      throw new Error('Empty CSV data');
    }

    // Extract header
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine);
    
    // Parse rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          // Try to convert numeric values
          const value = values[index] || '';
          if (value === '') {
            row[header] = null;
          } else if (!isNaN(value) && !isNaN(parseFloat(value))) {
            row[header] = parseFloat(value);
          } else {
            row[header] = value;
          }
        });
        rows.push(row);
      }
    }

    return {
      data: csvText,
      columns: headers,
      rows,
      rowCount: rows.length,
      columnCount: headers.length,
      isSimulated: false
    };
  } catch (error) {
    console.error('Failed to parse CSV data:', error);
    
    // Return an error object that can be displayed to the user
    return {
      error: true,
      message: error.message || 'Failed to parse CSV data',
      columns: ['Error'],
      rows: [{ 'Error': `Failed to parse CSV data: ${error.message}` }],
      rowCount: 1,
      columnCount: 1
    };
  }
};

/**
 * Parse a single line of CSV text, handling quoted values properly
 * 
 * @param {string} line - The CSV line to parse
 * @returns {string[]} Array of values from the line
 */
function parseCSVLine(line) {
  const values = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote inside quotes
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      // End of value
      values.push(currentValue);
      currentValue = '';
    } else {
      // Add character to current value
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
}

export default csvToDataFrame;